export const percentagesToBoundaries = (percentages: number[]) => {
  const boundaries: number[] = [];
  let running = 0;

  for (const percentage of percentages.slice(0, -1)) {
    running += percentage;
    boundaries.push(running);
  }

  return boundaries;
};

export const boundariesToPercentages = (boundaries: number[]) => {
  const sorted = boundaries.toSorted((a, b) => a - b);
  const percentages: number[] = [];
  let previous = 0;

  for (const boundary of sorted) {
    percentages.push(boundary - previous);
    previous = boundary;
  }

  percentages.push(100 - previous);
  return percentages;
};

export const applyPercentages = <T extends { percentage: number }>(
  items: T[],
  nextPercentages: number[],
): T[] => items.map((item, index) => ({ ...item, percentage: nextPercentages[index] ?? 0 }));

export const normalizeWeightsToPercentages = (weights: number[]) => {
  if (weights.length === 0) {
    return [];
  }

  const sanitized = weights.map((weight) => Math.max(0, Math.round(weight)));
  const total = sanitized.reduce((sum, value) => sum + value, 0);

  if (total === 0) {
    const base = Math.floor(100 / sanitized.length);
    const remainder = 100 - base * sanitized.length;
    return sanitized.map((_, index) => base + (index < remainder ? 1 : 0));
  }

  const normalized = sanitized.map((value) => Math.floor((value / total) * 100));
  let remainder = 100 - normalized.reduce((sum, value) => sum + value, 0);

  const rankedIndexes = sanitized
    .map((value, index) => ({ index, value }))
    .toSorted((a, b) => b.value - a.value);

  for (const ranked of rankedIndexes) {
    if (remainder === 0) {
      break;
    }
    normalized[ranked.index] = (normalized[ranked.index] ?? 0) + 1;
    remainder -= 1;
  }

  return normalized;
};

export const percentagesToWeights = (percentages: number[]) =>
  percentages.map((value) => Math.max(0, value));

export const applyNormalizedWeights = <T extends { percentage: number }>(
  items: T[],
  weights: number[],
): T[] => applyPercentages(items, normalizeWeightsToPercentages(weights));
